<template>
    <div class="fabricRibbon" v-if="isFabric">
        <div
            v-if="connectedDatasource"
            class="datasource-info datasource-action"
            :class="{disabled: !canOpenSettings}"
            role="button"
            tabindex="0"
            @click="canOpenSettings && connectFabricDatasource()"
        >
            <i class="pi pi-database"></i>
            <div class="datasource-details">
                <span class="datasource-label">Connected to:</span>
                <span class="datasource-name">{{ connectedDatasource.sourceItemDisplayName }}</span>
                <span class="datasource-type">({{ connectedDatasource.sourceItemType }})</span>
            </div>
        </div>
        <div
            v-if="connectedDatasource?.activitiesTable"
            class="datasource-info datasource-action"
            :class="{disabled: !canOpenSettings}"
            role="button"
            tabindex="0"
            @click="canOpenSettings && openFabricConfig()"
        >
            <i class="pi pi-table"></i>
            <div class="datasource-details">
                <span class="datasource-label">Activity table:</span>
                <span class="datasource-name">
                    {{ connectedDatasource.activitiesSchema || 'dbo' }}.{{ connectedDatasource.activitiesTable }}
                </span>
                <span v-if="connectedDatasource.activitiesColumn" class="datasource-type">
                    ({{ connectedDatasource.activitiesColumn }})
                </span>
            </div>
        </div>
    </div>

    <LoadingState v-if="graphLoading" overlay></LoadingState>
    <!--TODO, I will leave "lazy" prop here. This has been placed and removed a lot of times -->
    <!-- from now on, please write down the reason why you are removing it or resetting it -->
    <!-- now I've set lazy so that the business rules dialog is not visible in every tab -->
    <Tabs class="graphTabs" value="0" lazy>
        <TabList>
            <Tab value="0" data-cy="graph-tab">Graph</Tab>
            <Tab value="1" v-if="graphId && canEditGraph" data-cy="run-tab">Run</Tab>
            <Tab value="2" v-if="graphId && versions.length > 0" data-cy="versions-tab">Versions</Tab>
            <Tab value="3" v-if="graphId && canEditGraph" data-cy="notes-tab">
                <div class="flex items-center gap-2" data-cy="notes-tab">
                    <span class="whitespace-nowrap">Notes</span>
                    <Tag class="betaTag" rounded severity="warning" value="BETA"></Tag>
                </div>
            </Tab>
            <Tab value="4" v-if="contentPushEnabled && graph && graph.datamodel_id">
                <div class="flex items-center gap-2" data-cy="ems-tab">
                    <span class="font-bold whitespace-nowrap">EMS Assets</span>
                    <Tag class="betaTag" rounded severity="warning" value="BETA"></Tag>
                </div>
            </Tab>
        </TabList>
        <TabPanels>
            <TabPanel value="0" class="relative">
                <!-- The key will allow to remount the component when the value change -->
                <GraphEditor
                    v-if="graphDefinition"
                    :key="graphId + selectedVersion"
                    :graphId="graphId"
                ></GraphEditor>
                <Message v-else-if="!graphLoading" severity="warn" :closable="false">
                    Graph not available for this item ID.
                </Message>
            </TabPanel>
            <TabPanel value="1" v-if="graphId && canEditGraph">
                <GraphRun
                    v-if="graphDefinition"
                    :graphId="graphId"
                    :key="'run' + graphId"
                />
            </TabPanel>
            <TabPanel value="2" v-if="graphId">
                <GraphVersions :key="'versions_' + graphId"/>
            </TabPanel>
            <TabPanel value="3" v-if="graphId && canEditGraph">
                <GraphNotes :key="'notes_' + graphId"/>
            </TabPanel>
        </TabPanels>
    </Tabs>

    <div class="editModeButton flex items-center gap-2">
        <Message v-if="lockError" severity="error">{{ lockError }}</Message>
        <LoadingState v-if="lockLoading" small></LoadingState>
        <Button
            v-if="canEditGraph && !isLocked"
            :label="getEditMode ? 'Exit edit mode' : 'Edit mode'"
            :severity="getEditMode ? 'secondary' : 'primary'"
            :disabled="lockLoading"
            data-cy="edit-mode-button"
            :icon="getEditMode ? 'pi pi-times' : 'pi pi-pencil'"
            @click="toggleEditMode"
            rounded
        />
        <Button
            v-if="isLocked"
            icon="pi pi-lock"
            :disabled="lockLoading"
            @click="checkLockedBy"
            data-cy="locked-button"
            text
            rounded
            v-tooltip.bottom="
        'Another user is editing this graph. Click here to check again'
      "
        />
    </div>
    <Dialog
        v-model:visible="getShowAskToStay"
        modal
        :closable="false"
        style="width: 400px"
        header="Do you want to stay in edit mode?"
    >
        <div class="flex items-center mb-2">
            <i class="pi pi-exclamation-triangle mr-2" style="font-size: 2rem"></i>
            <span
            >Edit mode will automatically turn off in {{ timeLeft }} seconds</span
            >
        </div>
        <div class="flex justify-end gap-2">
            <Button rounded icon="pi pi-times" label="Exit" class="p-button-text" @click="toggleEditMode"/>
            <Button rounded icon="pi pi-check" label="Stay" class="p-button-raised" @click="lockGraph"/>
        </div>
    </Dialog>
    <Dialog
        v-model:visible="fabricConfigVisible"
        modal
        style="width: 520px"
        header="Select activity table"
        :closable="!fabricConfigLoading"
    >
        <div class="fabric-config">
            <div class="fabric-config-row">
                <span class="fabric-config-label">Table</span>
                <Select
                    v-model="selectedActivityTable"
                    :options="fabricTables.map(table => table.fullName)"
                    placeholder="Select table"
                    :loading="fabricTablesLoading"
                    :disabled="fabricTablesLoading || fabricConfigLoading"
                    @update:modelValue="onActivityTableChange"
                >
                    <template #empty>
                        {{ fabricTablesLoading ? 'Loading tables...' : 'No tables available' }}
                    </template>
                </Select>
            </div>
            <div class="fabric-config-row">
                <span class="fabric-config-label">Column</span>
                <Select
                    v-model="selectedActivityColumn"
                    :options="fabricColumns"
                    placeholder="Select column"
                    :loading="fabricColumnsLoading"
                    :disabled="!selectedActivityTable || fabricColumnsLoading || fabricConfigLoading"
                    @update:modelValue="onActivityColumnChange"
                >
                    <template #empty>
                        {{ fabricColumnsLoading ? 'Loading columns...' : 'Select a table first' }}
                    </template>
                </Select>
            </div>
            <Message v-if="fabricConfigError" severity="error" :closable="false">
                {{ fabricConfigError }}
            </Message>
        </div>
        <template #footer>
            <div class="flex justify-end gap-2">
                <Button
                    rounded
                    label="Close"
                    severity="secondary"
                    :disabled="fabricConfigLoading"
                    @click="fabricConfigVisible = false"
                />
            </div>
        </template>
    </Dialog>
</template>
<script setup>
import {computed, defineAsyncComponent, watch, ref, onMounted} from "vue";
import {useStore} from "vuex";

const store = useStore();
import {useRouter} from "vue-router";
import {getFabricClient, getFabricItemObjectId, isFabricContext} from "@/src/utils/fabricContext";

const GraphEditor = defineAsyncComponent(() =>
    import("./editor/GraphEditor.vue")
);
const GraphRun = defineAsyncComponent(() => import("./run/GraphRun"));
import {GRAPH_API_TYPE} from "@/src/model/apiTypes";
import {DATA_API_TYPE} from "@/src/model/apiTypes";

const GraphNotes = defineAsyncComponent(() => import("./GraphNotes"));
const GraphVersions = defineAsyncComponent(() => import("./GraphVersions"));
const router = useRouter();

const contentPushEnabled = import.meta.env.VITE_APP_CONTENT_PUSH_ENABLED === "true";

// Set once before Vue mounts (from ui.ts bootstrap path) — no reactivity needed
const fabricGraphId = getFabricItemObjectId();

const graphId = computed(() => {
    return fabricGraphId
});
const graph = computed(() => store.getters["repoStore/getGraph"]);
const hasMatchingGraph = computed(() => {
    if (!graphId.value || !graph.value) {
        return false;
    }
    return String(graph.value.workload_id) === String(graphId.value);
});
const graphDefinition = computed(
    () => store.getters["repoStore/getGraphDefinition"]
);
const canEditGraph = true
const selectedVersion = computed(
    () => store.getters["repoStore/getSelectedVersion"]
);

const graphLoading = computed(() =>
    store.getters.isLoading(GRAPH_API_TYPE.GET_GRAPH)
);

const lockError = ref(null);

const error = computed(() => store.getters.getError);
const timeLeft = ref(60);

const setLockError = (e) => {
    lockError.value = e.response.data.error.message;
    setTimeout(() => {
        lockError.value = null;
    }, 5000);
};

const checkLockedBy = () => store.dispatch("graphStore/checkLockedBy");

const versions = computed(() => store.getters["repoStore/getVersions"])
const isFabric = computed(() => isFabricContext());

const canOpenSettings = computed(() => {
    return !!graphId.value;
});

const connectedDatasource = computed(() => {
    if (!graph.value?.data_source) {
        return null;
    }
    const datasource = graph.value.data_source;
    // Check if it's a Fabric datasource with required fields
    if (datasource.sourceType === 'fabric-onelake' &&
        datasource.sourceItemId &&
        datasource.sourceItemDisplayName) {
        return datasource;
    }
    return null;
});

const fabricConfigVisible = ref(false);
const fabricTables = ref([]);
const fabricColumns = ref([]);
const selectedActivityTable = ref("");
const selectedActivityColumn = ref("");
const fabricConfigError = ref("");

const fabricConfigLoading = computed(() =>
    store.getters.isLoading([
        DATA_API_TYPE.GET_FABRIC_TABLES,
        DATA_API_TYPE.GET_FABRIC_COLUMNS,
    ])
);

const fabricTablesLoading = computed(() =>
    store.getters.isLoading([DATA_API_TYPE.GET_FABRIC_TABLES])
);

const fabricColumnsLoading = computed(() =>
    store.getters.isLoading([DATA_API_TYPE.GET_FABRIC_COLUMNS])
);


const connectFabricDatasource = async () => {
    try {
        const client = getFabricClient();
        if (!client?.datahub?.openDialog) {
            return;
        }

        const result = await client.datahub.openDialog({
            supportedTypes: ["Lakehouse", "Warehouse", "KQLDatabase"],
            multiSelectionEnabled: false,
            dialogDescription: "Select a Fabric datasource for PacGraph",
            workspaceNavigationEnabled: true,
            hostDetails: {
                experience: "PacGraph",
                scenario: "Connect datasource",
            },
        });

        const selected = result?.selectedDatahubItem?.[0];
        if (!selected || !graph.value) {
            return;
        }

        await store.dispatch("graphStore/updateGraphFields", {
            data: {
                data_source: {
                    sourceType: "fabric-onelake",
                    sourceWorkspaceId: selected.workspaceObjectId || "",
                    sourceItemId: selected.itemObjectId || "",
                    sourceItemDisplayName: selected.datahubItemUI?.displayName || "",
                    sourceItemType: selected.datahubItemUI?.itemType || "",
                }
            },
        });

        fabricConfigVisible.value = true;
        await hydrateFabricConfiguration(selected);
    } catch (error) {
        console.error("connectFabricDatasource failed", error);
    }
};

const openFabricConfig = async () => {
    if (!connectedDatasource.value) {
        return;
    }
    fabricConfigVisible.value = true;
    await hydrateFabricConfiguration();
};

const hydrateFabricConfiguration = async (selected = null) => {
    if (!graph.value?.data_source) {
        return;
    }

    fabricConfigError.value = "";
    try {
        const dataSource = graph.value.data_source || {};
        const workspaceId = selected?.workspaceObjectId || dataSource.sourceWorkspaceId || "";
        const itemId = selected?.itemObjectId || dataSource.sourceItemId || "";
        const sqlEndpointId = selected?.sqlEndpointId || dataSource.sourceSqlEndpointId || "";

        const tableResult = await store.dispatch("repoStore/getFabricTables", {
            workspaceId,
            sqlEndpointId,
            itemId,
        });
        const resolvedSqlEndpointId = tableResult?.sqlEndpointId || sqlEndpointId || "";
        const resolvedSqlEndpoint = tableResult?.sqlEndpoint || "";
        const resolvedSqlDatabase = tableResult?.sqlDatabase || "";
        const tables = tableResult?.tables || [];

        if (resolvedSqlEndpointId || resolvedSqlEndpoint || resolvedSqlDatabase) {
            await store.dispatch("graphStore/updateGraphFields", {
                data: {
                    data_source: {
                        ...dataSource,
                        sourceSqlEndpointId: resolvedSqlEndpointId,
                        sourceSqlEndpoint: resolvedSqlEndpoint || dataSource.sourceSqlEndpoint || "",
                        sourceSqlDatabase: resolvedSqlDatabase || dataSource.sourceSqlDatabase || "",
                    },
                },
            });
        }

        fabricTables.value = Array.isArray(tables) ? tables : [];
        if (!Array.isArray(tables)) {
            fabricConfigError.value = "Unable to load Fabric tables.";
            return;
        }

        const existingSchema = dataSource.activitiesSchema || "";
        const existingTable = dataSource.activitiesTable || "";
        const existingFullName = existingSchema && existingTable ? `${existingSchema}.${existingTable}` : "";
        if (existingFullName) {
            selectedActivityTable.value = existingFullName;
            await onActivityTableChange(existingFullName);
            const existingColumn = dataSource.activitiesColumn || "";
            if (existingColumn) {
                selectedActivityColumn.value = existingColumn;
            }
        } else {
            selectedActivityTable.value = "";
            selectedActivityColumn.value = "";
            fabricColumns.value = [];
        }
    } catch (error) {
        console.error("hydrateFabricConfiguration failed", error);
        fabricConfigError.value = "Unable to load Fabric metadata.";
    }
};

const onActivityTableChange = async (value) => {
    const selectedValue = value || selectedActivityTable.value;
    if (!selectedValue) {
        selectedActivityColumn.value = "";
        fabricColumns.value = [];
        return;
    }

    const dataSource = graph.value?.data_source || {};
    const workspaceId = dataSource.sourceWorkspaceId || "";
    const itemId = dataSource.sourceItemId || "";
    const sqlEndpointId = dataSource.sourceSqlEndpointId || "";
    if (!workspaceId || !itemId) {
        fabricConfigError.value = "Unable to load Fabric metadata.";
        return;
    }

    const table = fabricTables.value.find(item => item.fullName === selectedValue);
    if (!table) {
        return;
    }

    await store.dispatch("graphStore/updateGraphFields", {
        data: {
            data_source: {
                ...graph.value.data_source,
                activitiesSchema: table.schema,
                activitiesTable: table.name,
            },
        },
    });

    const columns = await store.dispatch("repoStore/getFabricColumns", {
        workspaceId,
        sqlEndpointId,
        itemId,
        schema: table.schema,
        table: table.name,
    });
    fabricColumns.value = Array.isArray(columns) ? columns : [];
    selectedActivityColumn.value = "";
};

const onActivityColumnChange = async (value) => {
    const selectedValue = value || selectedActivityColumn.value;
    if (!selectedValue) {
        return;
    }

    await store.dispatch("graphStore/updateGraphFields", {
        data: {
            data_source: {
                ...graph.value.data_source,
                activitiesColumn: selectedValue,
            },
        },
    });
};

watch(error, (val) => {
    if (
        val &&
        val.code == "ERR_BAD_REQUEST" &&
        val.response.data.error.name == "GRAPH_LOCKED"
    ) {
        // the graph is locked by another user
        checkLockedBy();
        setLockError(val);
    }
    if (
        val &&
        val.code == "ERR_BAD_REQUEST" &&
        val.response.data.error.name == "GRAPH_NOT_LOCKED"
    ) {
        // an update call was performed on a graph that is not locked
        // we need to get the latest version of the graph and exit edit mode
        // this situation happen when the lock goes to timeout
        window.location.reload();
    }
});

const lockLoading = computed(() =>
    store.getters.isLoading([
        GRAPH_API_TYPE.LOCK_GRAPH,
        GRAPH_API_TYPE.UNLOCK_GRAPH,
        GRAPH_API_TYPE.GET_LOCK,
    ])
);
const isLockedByAnotherUser = computed(() =>
    store.getters["graphStore/isLockedByAnotherUser"]()
);
const lockTimedOut = computed(() => store.getters["graphStore/lockTimedOut"]);
const isLocked = computed(
    () => isLockedByAnotherUser.value && !lockTimedOut.value
);
const getEditMode = computed(() => store.getters["graphStore/getEditMode"]);

watch(isLocked, (val) => {
    // in case locked state changes but the user is still in edit mode
    if (val && getEditMode.value) {
        window.location.reload();
    }
});
onMounted(async () => {
    // Ensure selected graph is loaded also when route param is a string ID (Fabric item ID).
    if (graphId.value && !hasMatchingGraph.value) {
        try {
            console.log("mounted", graphId.value)
            const currentGraph = await store.dispatch("repoStore/getGraph", {graphId: graphId.value});
            await store.dispatch('repoStore/setSelectedGraph', currentGraph)
        } catch (error) {
            console.error("Graph bootstrap failed:", error)
        }
    } else {
        // this will set the selected graph in the store, especially needed when getting there via routing (e.g., pressing back button on browser)
        store.dispatch('repoStore/setSelectedGraph', graph.value)
    }
    window.onbeforeunload = () => {
        if (getEditMode.value) {
            // release the lock when the user refreshes the page
            store.dispatch("graphStore/unlockGraph");
        }
    };
});

watch(graphId, async () => {
    if (graphId.value && !hasMatchingGraph.value) {
        console.log("watch graphId", graphId.value)
        // in case the user is navigating from one graph to another by changing the URL
        const newGraph = await store.dispatch("repoStore/getGraph", {graphId: graphId.value});
        store.dispatch('repoStore/setSelectedGraph', newGraph)
    }
})

const toggleEditMode = () => store.dispatch("graphStore/toggleEditMode");

const getShowAskToStay = computed(
    () => store.getters["graphStore/getShowAskToStay"]
);
const lockGraph = () => store.dispatch("graphStore/lockGraph");

let interval = null;
watch(getShowAskToStay, (val) => {
    if (val) {
        interval = setInterval(() => {
            timeLeft.value--;
            if (timeLeft.value === 0) {
                clearInterval(interval);
            }
        }, 1000);
    } else {
        clearInterval(interval);
        timeLeft.value = 60;
    }
});
</script>

<style scoped>
.graphTabs {
  height: 94% !important;
}
.betaTag {
    margin-left: -10px;
    margin-top: -15px;
    height: 12px;
    font-size: 8px;
}

.graphTabView {
    height: calc(100% - 46px);
}

:deep(.p-tabview-panels),
:deep(.p-tabview-panel) {
    height: 100%;
}

.editModeButton {
    position: absolute;
    top: 6px;
    right: 14px;
}

.fabricRibbon {
    display: flex;
    gap: 12px;
    align-items: center;
    padding: 8px 12px;
    border-bottom: 1px solid #e5e5e5;
}

.datasource-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    background-color: #f5f5f5;
    border-radius: 6px;
    border: 1px solid #d5d5d5;
}

.datasource-action {
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}

.datasource-action:hover {
    border-color: #c0c0c0;
    background-color: #efefef;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.04);
}

.datasource-action.disabled {
    cursor: not-allowed;
    opacity: 0.6;
    box-shadow: none;
}

.datasource-info i {
    color: #666;
    font-size: 1.2rem;
}

.datasource-details {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.9rem;
}

.datasource-label {
    color: #666;
    font-weight: 500;
}

.datasource-name {
    color: #333;
    font-weight: 600;
}

.datasource-type {
    color: #888;
    font-style: italic;
}

.fabric-config {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.fabric-config-row {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.fabric-config-label {
    font-weight: 600;
    color: #444;
}
</style>
